import { Box, darken, lighten, styled } from '@mui/system';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Grid } from '@mui/material';
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridRenderEditCellParams,
  GridRowClassNameParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import { useSettingsDataGridStyles } from 'components/atoms/BaseDataGrid/Style';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import ImpactIcon from 'components/StudyDashboard/QCMarkers/ImpactIcon';
import { ADMIN_TABLE_DEFAULT_PAGE_SIZE, ADMIN_TABLE_PAGE_SIZE_OPTIONS } from 'constants/adminTables';
import { CellRulePanelWithRules, CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { QCImpactId } from 'interfaces/qcMarkers';
import {
  compact,
  concat,
  every,
  filter,
  find,
  findIndex,
  forEach,
  includes,
  isEmpty,
  map,
  size,
  slice,
  some,
} from 'lodash';
import React, { ReactNode, useMemo } from 'react';
import { useStainTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useCellTypeOptions } from 'utils/queryHooks/useCellTypeOptions';
import useStudy from 'utils/useStudy';
import CellRuleDisplay from './CellRuleGridCell/CellRuleDisplay';
import CellRuleDropdown from './CellRuleGridCell/CellRuleDropdown';
import CellRuleLegend from './CellRuleLegend';
import CellRuleSelectionToolbar, { CellRuleSelectionToolbarProps } from './CellRuleTableSelectionToolbar';
import QcMarkerLegend from './QcMarkerLegend';
import { CellRulesEditableToolbarProps, CustomToolbarWithExportAndImport } from './Toolbars/ExportAndImportToolbar';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .row-warning': {
    backgroundColor: theme.palette.mode == 'light' ? theme.palette.warning.light : theme.palette.warning.dark,
    '&:hover': {
      backgroundColor:
        theme.palette.mode == 'light'
          ? darken(theme.palette.warning.light, 0.1)
          : lighten(theme.palette.warning.dark, 0.1),
    },
  },
  '& .sticky-left': {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
  },
  '& .vertical-header .MuiDataGrid-columnHeaderTitleContainerContent': {
    overflow: 'visible',
  },
  '&.MuiDataGrid-root .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainerContent': {
    fontWeight: 'normal',
    overflow: 'visible',
    width: '100%',
  },
  '& .selected-cell': {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? lighten(theme.palette.primary.main, 0.3)
        : darken(theme.palette.primary.main, 0.3),
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
}));

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends CellRuleSelectionToolbarProps, CellRulesEditableToolbarProps {}
}
interface CellRulesDataGridProps {
  studyId?: string;
  panelId: string;
  stainTypeIds?: string[] | null;
  markerDisplayNames?: Record<string, string>;
  cellTypeHeaderAction?: ReactNode;
  cellTypeDisplayNames?: Record<string, string>;
  cellTypeCaptions?: Record<string, string>;
  cellTypeIds?: string[] | null;
  rules?: MinimalCellRule[] | null;
  isLoading: boolean;
  editable?: boolean;
  // only if editable is true
  onCellValueChange?: (cellTypeId: string, stainTypeId: string, value: CellRuleValue) => void;
  onAddCellTypes?: (cellTypeIds: string[]) => void;
  onImport?: (newStainTypeIds: string[], newCellTypeIds: string[], newRules: MinimalCellRule[]) => void;
  onRemoveStainType?: (stainTypeId: string) => void;
  onRemoveCellType?: (cellTypeId: string) => void;
  selectedCells?: MinimalCellRule[];
  onSelectCellRules?: (rules: MinimalCellRule[]) => void;
  onSelectionFinished?: () => void;
  isCompact?: boolean;
  handleShowCompactToggle?: (isCompacting: boolean) => void;
  adminTableLayout?: boolean;
}

export const cellRuleWidth = 30;

const rulesToTable = (
  rules: CellRulePanelWithRules['rules'] | null | undefined,
  stainTypeIds: string[],
  cellTypeIds: string[]
) => {
  const ruleRows = rules ?? [];
  let table: Record<string, Record<string, number>> = {};

  forEach(cellTypeIds, (cellTypeId) => {
    table[cellTypeId] = {};
    forEach(stainTypeIds, (stainTypeId) => {
      table[cellTypeId][stainTypeId] = CellRuleValue.Indifferent;
    });
  });

  forEach(ruleRows, (rule) => {
    const { cellTypeId, stainTypeId, ruleValue } = rule;
    if (!table[cellTypeId]) {
      table[cellTypeId] = {};
    }
    table[cellTypeId][stainTypeId] = ruleValue;
  });

  return table;
};

const CellRulesDataGrid: React.FC<CellRulesDataGridProps> = ({
  studyId,
  panelId,
  stainTypeIds,
  markerDisplayNames,
  cellTypeHeaderAction,
  cellTypeDisplayNames,
  cellTypeCaptions,
  cellTypeIds,
  rules,
  isLoading,
  editable = false,
  onCellValueChange,
  onAddCellTypes,
  onRemoveStainType,
  onRemoveCellType,
  selectedCells,
  onSelectCellRules,
  onSelectionFinished,
  isCompact,
  handleShowCompactToggle,
  onImport,
  adminTableLayout = false,
}) => {
  const columnIds = stainTypeIds ?? [];
  const rowIds = cellTypeIds ?? [];
  const ruleRows = rules ?? [];
  const table = useMemo(() => rulesToTable(ruleRows, columnIds, rowIds), [ruleRows, columnIds, rowIds]);
  const { data: cellTypesOptions, isLoading: isLoadingCellTypes } = useCellTypeOptions();
  const { stainTypeOptions } = useStainTypeOptions();

  const isSpecificStudySelected = studyId && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const { study } = useStudy(studyId, { enabled: isSpecificStudySelected });
  const qcMarkersStudySettings = study?.settings?.qcMarkersSettings;

  const settingsDataGridStyles = useSettingsDataGridStyles();

  const getCellTypeDisplayName = (cellTypeId: string) => {
    const override = cellTypeDisplayNames?.[cellTypeId];
    return (
      (override !== cellTypeId ? override : undefined) ||
      find(cellTypesOptions, (cellTypeOption) => cellTypeOption.id === cellTypeId)?.displayName
    );
  };

  const rows = useMemo(
    () =>
      map(rowIds, (cellTypeId) => {
        const row = { id: cellTypeId, ...table[cellTypeId] };
        return row;
      }),
    [rowIds, table]
  );

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Cell Type',
      width: cellTypeHeaderAction ? 180 : 150,
      editable: false,
      valueFormatter: (value) => getCellTypeDisplayName(value) || value,
      renderHeader: cellTypeHeaderAction
        ? () => (
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Box component="span">Cell Type</Box>
              {cellTypeHeaderAction}
            </Box>
          )
        : undefined,
      renderCell: (params) => {
        const displayName =
          getCellTypeDisplayName(params.value) ||
          (isLoadingCellTypes ? 'Loading cell class...' : 'Cell class unavailable');
        const caption = cellTypeCaptions?.[params.value];

        const hasQCMarkerWithRule = some(columnIds, (stainTypeId) => {
          const hasQCMarker = !isEmpty(qcMarkersStudySettings?.[stainTypeId]);
          const ruleValue = table[params.value]?.[stainTypeId];
          return (
            hasQCMarker &&
            (ruleValue === CellRuleValue.MustBePositive || ruleValue === CellRuleValue.OneOfTwoMustBePositive)
          );
        });

        return (
          <Box
            title={params.value}
            onMouseUp={(event) => {
              // if middle mouse button is clicked, we want to delete the stain type
              if (event.button === 1 && editable && onRemoveCellType) {
                onRemoveCellType(params.value);
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              justifyContent: 'space-between',
            }}
          >
            {displayName}
            {caption ? ` ${caption}` : null}
            {hasQCMarkerWithRule && (
              <ErrorOutlineIcon
                fontSize="small"
                sx={{
                  mr: 1,
                }}
              />
            )}
          </Box>
        );
      },
      headerClassName: 'sticky-left',
      cellClassName: 'sticky-left',
    },
    ...compact(
      map(columnIds, (stainTypeId) => {
        const catalogStainType = find(stainTypeOptions, (stainTypeOption) => stainTypeOption.id === stainTypeId);
        const explicitDisplayName = markerDisplayNames?.[stainTypeId];
        const stainType = explicitDisplayName
          ? { id: stainTypeId, displayName: explicitDisplayName }
          : catalogStainType ?? { id: stainTypeId, displayName: stainTypeId };

        const qcMarker = qcMarkersStudySettings ? qcMarkersStudySettings[stainTypeId] || '' : '';

        return {
          field: stainType.id,
          sortable: false,
          editable,
          headerName: stainType.displayName,
          headerAlign: 'center',
          align: 'center',
          width: cellRuleWidth,
          resizable: false,
          renderEditCell: (params: GridRenderEditCellParams) => (
            <CellRuleDropdown
              cellRule={{
                cellRulePanelId: panelId,
                cellTypeId: params.row.id as string,
                stainTypeId: stainType.id,
                ruleValue: params.value as CellRuleValue,
                id: `${panelId}-${params.row.id}-${stainType.id}`,
              }}
              onChange={onCellValueChange}
            />
          ),
          renderCell: (params) => (
            <Box width={cellRuleWidth}>
              <CellRuleDisplay ruleValue={params.value as CellRuleValue} />
            </Box>
          ),
          // the custom header is so the text is rotated 90 degrees, this allows for a more compact data grid display
          renderHeader: () => (
            <Box
              onMouseDown={(event) => {
                // if middle mouse button is clicked, we want to delete the stain type
                if (event.button === 1 && editable && onRemoveStainType) {
                  onRemoveStainType?.(stainType.id);
                }
              }}
              sx={{
                transform: 'rotate(-90deg)',
                width: '100%',
                height: '100%',
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <ImpactIcon qcImpactId={qcMarker} emptyIconPlaceholder={true} rotate={90} />
              {stainType.displayName}
            </Box>
          ),
          headerClassName: 'vertical-header',
        } as GridColDef;
      })
    ),
  ];

  const getRowClassName = (params: GridRowClassNameParams<GridValidRowModel>) => {
    // if there are no rules for this cell type, we want to highlight it
    const rowRules = map(columnIds, (stainTypeId) => params.row[stainTypeId]);
    if (every(rowRules, (value) => value === CellRuleValue.Indifferent)) {
      return 'row-warning';
    }
  };

  const getCellClassName = (params: GridCellParams) => {
    const cellTypeId = params.row.id as string;
    const stainTypeId = params.field as string;
    if (selectedCells && find(selectedCells, { cellTypeId, stainTypeId })) {
      return 'selected-cell';
    }
  };

  const allowSelect = Boolean(onSelectCellRules) && Boolean(selectedCells);

  const handleCellClick = (params: GridCellParams) => {
    if (!allowSelect) return;

    const cellTypeId = params.row.id as string;
    const stainTypeId = params.field as string;
    const ruleIndex = findIndex(selectedCells, { cellTypeId, stainTypeId });

    if (ruleIndex > -1) {
      onSelectCellRules([...slice(selectedCells, 0, ruleIndex), ...slice(selectedCells, ruleIndex + 1)]);
    } else {
      const rule = find(ruleRows, { cellTypeId, stainTypeId });
      const ruleToAdd = rule ? rule : { cellTypeId, stainTypeId, ruleValue: CellRuleValue.Indifferent };
      // add the cell
      onSelectCellRules([...selectedCells, ruleToAdd]);
    }
  };

  const handleSelectRuleValue = (ruleValue: CellRuleValue) => {
    if (!allowSelect) return;

    const cellsWithSelectedRuleValue = filter(ruleRows, { ruleValue });
    if (every(cellsWithSelectedRuleValue, (cell) => find(selectedCells, cell))) {
      // if all cells with the selected rule value are already selected, we want to deselect them
      const finalSelectedCells = filter(selectedCells, (cell) => !find(cellsWithSelectedRuleValue, cell));
      onSelectCellRules(finalSelectedCells);
    } else {
      // if not all cells with the selected rule value are selected, we want to select them
      const finalSelectedCells = concat(selectedCells, cellsWithSelectedRuleValue);
      onSelectCellRules(finalSelectedCells);
    }
  };

  const activeQcImpacts = [
    ...new Set(
      columnIds.flatMap((stainTypeId) => {
        const impact = qcMarkersStudySettings?.[stainTypeId];
        const usedByActiveRule = rowIds.some((cellTypeId) => {
          const ruleValue = table[cellTypeId]?.[stainTypeId];
          return ruleValue === CellRuleValue.MustBePositive || ruleValue === CellRuleValue.OneOfTwoMustBePositive;
        });
        return usedByActiveRule && (impact === QCImpactId.FLAGGED || impact === QCImpactId.EXCLUDE) ? [impact] : [];
      })
    ),
  ];

  const CellRuleLegendSlot = () => (
    <Grid container>
      <CellRuleLegend />
      <QcMarkerLegend activeImpacts={activeQcImpacts} />
    </Grid>
  );

  const CellRuleAdminFooterSlot = () => (
    <GridFooterContainer sx={{ justifyContent: 'space-between' }}>
      <CellRuleLegendSlot />
      <GridPagination />
    </GridFooterContainer>
  );

  const slots = {
    footer: adminTableLayout ? CellRuleAdminFooterSlot : CellRuleLegendSlot,
    toolbar: allowSelect ? CellRuleSelectionToolbar : CustomToolbarWithExportAndImport,
  };

  const slotProps = {
    ...(allowSelect
      ? {
          toolbar: {
            onSelectRuleValue: handleSelectRuleValue,
            finishSelectionText: 'Add to assignment',
            onSelectionFinished,
            isCompact,
            handleShowCompactToggle,
          },
        }
      : {
          toolbar: {
            editable,
            onImport: editable ? onImport : undefined,
            studyId,
            panelId,
            cellTypeIds: rowIds,
            onAddCellTypes,
            classAdditionDisabled: isLoading,
          },
        }),
  };

  return (
    <StyledDataGrid
      autoHeight={adminTableLayout}
      disableVirtualization
      pagination={adminTableLayout ? true : undefined}
      initialState={
        adminTableLayout
          ? {
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: ADMIN_TABLE_DEFAULT_PAGE_SIZE,
                },
              },
            }
          : undefined
      }
      pageSizeOptions={adminTableLayout ? ADMIN_TABLE_PAGE_SIZE_OPTIONS : undefined}
      rows={rows}
      columns={columns}
      slots={slots}
      slotProps={slotProps}
      getRowClassName={getRowClassName}
      getCellClassName={getCellClassName}
      columnHeaderHeight={200}
      loading={isLoading && Boolean(panelId)}
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableRowSelectionOnClick
      density="compact"
      onCellClick={handleCellClick}
      sx={{
        ...settingsDataGridStyles,
        ...(size(rowIds) === 0 && {
          '& .MuiDataGrid-overlayWrapper': {
            '& .MuiDataGrid-overlayWrapperInner': {
              height: 'auto',
            },
          },
        }),
      }}
    />
  );
};

export default CellRulesDataGrid;
